import { z } from 'zod';

// A catalog theme's versions, as Shopify numbers its themes. A store's copy is pinned to the version it
// installed and is offered Update when the theme's newest version is ahead of it; nothing moves a store on
// by itself.

/** Three numbers, major.minor.patch, as a theme's version is written in its theme.json. */
export const ThemeVersionSchema = z.string().regex(/^\d{1,3}\.\d{1,3}\.\d{1,3}$/, 'A theme version is three numbers, like 1.2.0');

/** What a version changed, one short line each, as the admin's Update dialog lists them. */
export const ThemeReleaseNotesSchema = z.array(z.string().trim().min(1).max(200)).max(12);

/** One published version of a theme, as the admin lists it. */
export const ThemeReleaseSchema = z.object({
  version: ThemeVersionSchema,
  notes: ThemeReleaseNotesSchema,
  createdAt: z.string()
});

export type ThemeReleaseDto = z.infer<typeof ThemeReleaseSchema>;

// Orders two theme versions: negative when the first is older, zero when they are the same, positive when newer.
export function compareThemeVersions(a: string, b: string): number {
  const left = a.split('.').map(Number);
  const right = b.split('.').map(Number);
  for (let part = 0; part < 3; part += 1) {
    const difference = (left[part] || 0) - (right[part] || 0);
    if (difference !== 0) return Math.sign(difference);
  }
  return 0;
}

// Reports whether a store's copy is behind its theme's newest version, which is when Update is offered.
export const themeUpdateAvailable = (installed: string | null | undefined, latest: string): boolean =>
  typeof installed === 'string' && installed !== '' && compareThemeVersions(installed, latest) < 0;
